import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "selectedContainer", "selectedList", "stepForm", "hiddenInputs", "suggestionsContainer", "modal", "emptyState"]
  static values = {
    ingredients: { type: Array, default: [] }
  }

  connect() {
    this.searchTimeout = null

    // Listen for turbo frame load to hide loading state
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this.boundHandleFrameLoad)
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.boundHandleFrameLoad)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  handleFrameLoad(event) {
    if (event.target.id === 'ingredient_search_results') {
      // Results loaded
    }
  }

  // Open the search modal
  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.style.display = 'flex'
      document.body.style.overflow = 'hidden'

      // Focus the search input after a brief delay
      setTimeout(() => {
        if (this.hasSearchInputTarget) {
          this.searchInputTarget.focus()
        }
      }, 100)
    }
  }

  // Close the search modal
  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.style.display = 'none'
      document.body.style.overflow = ''

      // Clear search input and reset to empty state
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ''
      }
      this.showEmptyState()
    }
  }

  // Handle search input with debouncing
  handleSearchInput(event) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    const searchTerm = event.target.value.trim()

    if (searchTerm.length < 2) {
      this.showEmptyState()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(searchTerm)
    }, 250)
  }

  // Show empty state, hide results
  showEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden')
      this.searchResultsTarget.classList.remove('flex')
    }
  }

  // Show results, hide empty state
  showSearchResults() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
      this.searchResultsTarget.classList.add('flex')
    }
  }

  // Perform the search via Turbo frame
  performSearch(searchTerm) {
    const turboFrame = document.getElementById('ingredient_search_results')
    if (!turboFrame) return

    const url = new URL('/ingredients/search', window.location.origin)
    url.searchParams.set('search[term]', searchTerm)
    url.searchParams.set('search[exclude_user_ingredients]', 'true')
    url.searchParams.set('search[limit]', '5')
    url.searchParams.set('template[partial]', 'onboarding_ingredient')

    this.showSearchResults()
    turboFrame.src = url.toString()
  }

  // Handle click on ingredient in search results
  selectIngredient(event) {
    // Find the button that was clicked
    const button = event.target.closest('button')
    if (!button) return

    // Extract ingredient ID from the @click attribute
    const clickAttr = button.getAttribute('@click') || button.getAttribute('x-on:click')
    if (!clickAttr) return

    const idMatch = clickAttr.match(/selectedIngredient\s*=\s*(\d+)/)
    if (!idMatch) return

    const id = parseInt(idMatch[1])
    const name = button.querySelector('.text-base')?.textContent?.trim() || ''

    // Prevent default Alpine behavior
    event.stopPropagation()
    event.preventDefault()

    this.addIngredient(id, name)

    // Close modal after selection
    this.closeModal()
  }

  // Handle clicking a suggested ingredient
  selectSuggestion(event) {
    const button = event.currentTarget
    const id = parseInt(button.dataset.ingredientId)
    const name = button.dataset.ingredientName

    if (id && name) {
      this.addIngredient(id, name)
      this.updateSuggestionsVisibility()
    }
  }

  // Add an ingredient to the selection
  addIngredient(id, name) {
    // Check if already selected
    if (this.ingredientsValue.find(i => i.id === id)) {
      return
    }

    // Add to array
    this.ingredientsValue = [...this.ingredientsValue, { id, name }]

    // Update UI and form
    this.renderSelectedIngredients()
    this.updateHiddenInputs()
    this.updateSuggestionsVisibility()
    this.notifyFormChange()
  }

  // Remove an ingredient from the selection
  removeIngredient(event) {
    const id = parseInt(event.currentTarget.dataset.ingredientId)
    this.ingredientsValue = this.ingredientsValue.filter(i => i.id !== id)

    this.renderSelectedIngredients()
    this.updateHiddenInputs()
    this.updateSuggestionsVisibility()
    this.notifyFormChange()
  }

  // Render the selected ingredients chips
  renderSelectedIngredients() {
    if (!this.hasSelectedContainerTarget || !this.hasSelectedListTarget) return

    if (this.ingredientsValue.length === 0) {
      this.selectedContainerTarget.classList.add('hidden')
      this.selectedListTarget.innerHTML = ''
      return
    }

    this.selectedContainerTarget.classList.remove('hidden')
    this.selectedListTarget.innerHTML = this.ingredientsValue.map(ingredient => `
      <div class="flex items-center gap-2 px-3 py-2 bg-red-50 ring-1 ring-red-200 rounded-xl">
        <span class="text-sm text-red-800 font-medium">${this.escapeHtml(ingredient.name)}</span>
        <button type="button"
                data-action="avoided-ingredients#removeIngredient"
                data-ingredient-id="${ingredient.id}"
                class="text-red-400 hover:text-red-600">
          <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>
    `).join('')
  }

  // Update hidden form inputs
  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = this.ingredientsValue.map(ingredient => `
      <input type="hidden"
             name="onboarding_step[data][custom_avoided_ingredient_ids][${ingredient.id}]"
             value="1">
    `).join('')
  }

  // Notify the onboarding controller that the form has changed
  notifyFormChange() {
    if (!this.hasStepFormTarget) return

    // Dispatch change event for the onboarding controller
    this.stepFormTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Update suggestions visibility based on selected ingredients
  updateSuggestionsVisibility() {
    if (!this.hasSuggestionsContainerTarget) return

    // Hide suggestions that are already selected
    const selectedIds = this.ingredientsValue.map(i => i.id)
    const suggestionButtons = this.suggestionsContainerTarget.querySelectorAll('[data-ingredient-id]')

    suggestionButtons.forEach(button => {
      const id = parseInt(button.dataset.ingredientId)
      button.classList.toggle('hidden', selectedIds.includes(id))
    })

    // Hide the whole container if all suggestions are selected
    const visibleButtons = Array.from(suggestionButtons).filter(btn => !btn.classList.contains('hidden'))
    this.suggestionsContainerTarget.classList.toggle('hidden', visibleButtons.length === 0)
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
